// api-table — default skin module
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
  }

  .at-root {
    display: flex;
    flex-direction: column;
    gap: var(--run-api-table-gap, var(--run-space-semantic-xl, 32px));
  }

  .at-section {
    display: flex;
    flex-direction: column;
    gap: var(--run-space-semantic-sm, 8px);
  }

  .at-heading {
    margin: 0;
    font-size: var(--run-api-table-heading-size, var(--run-font-size-sm, 14px));
    font-weight: var(--run-font-semantic-weight-strong, 600);
    font-family: var(--run-font-semantic-family-body, inherit);
    color: var(--run-api-table-heading-color, var(--run-color-text-primary, #1e293b));
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .at-loading,
  .at-empty {
    font-size: var(--run-font-size-sm, 14px);
    font-family: var(--run-font-semantic-family-body, inherit);
    color: var(--run-color-text-secondary, #64748b);
    padding: var(--run-space-semantic-lg, 24px) 0;
  }

  .at-deprecated {
    opacity: var(--run-api-table-deprecated-opacity, 0.5);
  }

  .at-mono {
    font-family: var(--run-font-semantic-family-code, 'Courier New', monospace);
    font-size: 0.9em;
    background: var(--run-color-surface-subtle, #f8fafc);
    padding: 1px 4px;
    border-radius: var(--run-radius-semantic-control, 3px);
  }

  .at-type-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
`;

// Mapping of common type strings to badge variants
const TYPE_VARIANT_MAP = {
  string:  'neutral',
  boolean: 'info',
  number:  'success',
  object:  'warning',
  array:   'warning',
};

function typeVariant(type) {
  if (!type) return 'neutral';
  const base = type.toLowerCase().split(/[\s|<([]/)[0].trim();
  return TYPE_VARIANT_MAP[base] || 'neutral';
}

function esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function typeBadges(typeStr) {
  if (!typeStr) return '—';
  // Split union types on " | " for multi-chip display
  const parts = String(typeStr).split('|').map(s => s.trim()).filter(Boolean);
  if (parts.length === 1) {
    const v = typeVariant(parts[0]);
    return `<run-badge variant="${v}" pill size="sm">${esc(parts[0])}</run-badge>`;
  }
  const chips = parts.map(p => {
    const v = typeVariant(p);
    return `<run-badge variant="${v}" pill size="sm">${esc(p)}</run-badge>`;
  }).join('');
  return `<span class="at-type-chips">${chips}</span>`;
}

function mono(val) {
  if (val == null || val === '') return '—';
  return `<code class="at-mono">${esc(val)}</code>`;
}

function slotName(name) {
  return name === '' || name == null ? '(default)' : esc(name);
}

// ── Section renderers ────────────────────────────────────────────────────────

function renderProperties(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(p => `
    <run-table-row class="${p.deprecated ? 'at-deprecated' : ''}">
      <run-table-cell>${mono(p.name)}${p.deprecated ? ' <small style="color:var(--run-color-text-secondary,#64748b)">[deprecated]</small>' : ''}</run-table-cell>
      <run-table-cell>${p.attribute ? mono(p.attribute) : '—'}</run-table-cell>
      <run-table-cell>${typeBadges(p.type)}</run-table-cell>
      <run-table-cell>${p.default != null && p.default !== '' ? mono(p.default) : '—'}</run-table-cell>
      <run-table-cell>${esc(p.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-properties" class="at-section">
      <h3 part="section-heading" class="at-heading">Properties</h3>
      <run-table bordered size="s">
        <run-table-head>
          <run-table-column key="name"    width="160px">Property</run-table-column>
          <run-table-column key="attr"    width="140px">Attribute</run-table-column>
          <run-table-column key="type"    width="160px">Type</run-table-column>
          <run-table-column key="default" width="120px">Default</run-table-column>
          <run-table-column key="desc"    width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderSlots(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(s => `
    <run-table-row>
      <run-table-cell>${mono(slotName(s.name))}</run-table-cell>
      <run-table-cell>${esc(s.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-slots" class="at-section">
      <h3 part="section-heading" class="at-heading">Slots</h3>
      <run-table bordered size="s">
        <run-table-head>
          <run-table-column key="name" width="200px">Slot</run-table-column>
          <run-table-column key="desc" width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderEvents(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(e => `
    <run-table-row>
      <run-table-cell>${mono(e.name)}</run-table-cell>
      <run-table-cell>${e.type ? typeBadges(e.type) : '—'}</run-table-cell>
      <run-table-cell>${esc(e.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-events" class="at-section">
      <h3 part="section-heading" class="at-heading">Events</h3>
      <run-table bordered size="s">
        <run-table-head>
          <run-table-column key="name" width="200px">Event</run-table-column>
          <run-table-column key="type" width="200px">Detail Type</run-table-column>
          <run-table-column key="desc" width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderCssProps(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(p => `
    <run-table-row>
      <run-table-cell>${mono(p.name)}</run-table-cell>
      <run-table-cell>${p.default != null && p.default !== '' ? mono(p.default) : '—'}</run-table-cell>
      <run-table-cell>${esc(p.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-css-props" class="at-section">
      <h3 part="section-heading" class="at-heading">CSS Custom Properties</h3>
      <run-table bordered size="s">
        <run-table-head>
          <run-table-column key="name"    width="260px">Property</run-table-column>
          <run-table-column key="default" width="200px">Default</run-table-column>
          <run-table-column key="desc"    width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

function renderCssParts(rows) {
  if (!rows || !rows.length) return '';
  const bodyRows = rows.map(p => `
    <run-table-row>
      <run-table-cell>${mono(p.name)}</run-table-cell>
      <run-table-cell>${esc(p.description)}</run-table-cell>
    </run-table-row>
  `).join('');

  return `
    <div part="section section-css-parts" class="at-section">
      <h3 part="section-heading" class="at-heading">CSS Parts</h3>
      <run-table bordered size="s">
        <run-table-head>
          <run-table-column key="name" width="200px">Part</run-table-column>
          <run-table-column key="desc" width="1fr">Description</run-table-column>
        </run-table-head>
        <run-table-body>${bodyRows}</run-table-body>
      </run-table>
    </div>
  `;
}

const SECTION_RENDERERS = {
  properties: (c) => renderProperties(c.properties),
  slots:      (c) => renderSlots(c.slots),
  events:     (c) => renderEvents(c.events),
  'css-props': (c) => renderCssProps(c.cssProps),
  'css-parts': (c) => renderCssParts(c.cssParts),
};

// ── Main render ───────────────────────────────────────────────────────────────

export function render(host) {
  if (host._loading) {
    host.shadowRoot.innerHTML = `
      <div part="base" class="at-root">
        <div part="loading" class="at-loading">Loading…</div>
      </div>
    `;
    return;
  }

  const contract = host.contract;

  if (!contract) {
    host.shadowRoot.innerHTML = `
      <div part="base" class="at-root">
        <div part="empty" class="at-empty">No contract data.</div>
      </div>
    `;
    return;
  }

  const sections = host._activeSections
    .map(key => SECTION_RENDERERS[key]?.(contract) || '')
    .filter(Boolean)
    .join('');

  host.shadowRoot.innerHTML = `
    <div part="base" class="at-root">
      ${sections || '<div part="empty" class="at-empty">No sections to display.</div>'}
    </div>
  `;

  // After innerHTML replacement, force run-table children to re-sync
  // so column widths and _parentTable refs are established correctly.
  host.shadowRoot.querySelectorAll('run-table').forEach(t => t._syncChildren?.());
}
